import { Observable } from 'rxjs/Observable';
import { combineLatest, distinctUntilChanged, map } from 'rxjs/operators';
import { toObservable } from './helpers';
import { RootModel } from './root.model';
import { QueryParams } from './store.model';

/**
 * This file is the query class exposed to developers via datastore.list()
 *
 * It exposes a query that can be built up by chaining methods like:
 * ```lang=ts
 * this.datastore.list(
 *   'messages',
 *   this.auth.getUserId(),
 *   query => query.where('threadId', '==', this.thread.id).limit(50),
 * );
 * ```
 *
 * This API (and much of this class) is based on the interface from Firebase
 * and angularfire2, in particular:
 *   - firebase-js-sdk/packages/firestore/src/core/query.ts
 *   - firebase-js-sdk/packages/firestore/src/api/database.ts
 *   - firebase-js-sdk/packages/firestore-types/index.d.ts
 *   - angularfire2/src/firestore/interfaces.ts
 *   - angularfire2/src/firestore/firestore.ts
 */

/**
 * The direction of a `Query.orderBy()` clause is specified as 'desc' or 'asc'
 * (descending or ascending).
 */
// export type OrderByDirection = 'desc' | 'asc';

// interface OrderBy {
//   field: string;
//   direction: OrderByDirection;
// }

/**
 * Filter conditions in a `Query.where()` clause are specified using the
 * strings '<', '<=', '==', '>=', and '>'.
 */
// export type WhereFilterComparisonOp = '<' | '<=' | '==' | '>=' | '>';
export type WhereFilterComparisonOp = '==';
export type WhereFilterListOp = 'in';
export type WhereFilterOp = WhereFilterComparisonOp | WhereFilterListOp;

export class Query<K extends keyof RootModel> {
  // Should we make these readonly and make a new query each time?
  // private explicitOrderBy?: OrderBy;
  private params?: Observable<QueryParams<RootModel[K]>>;
  private limitValue?: Observable<number>;
  private offsetValue?: Observable<number>;

  where<T extends keyof RootModel[K]>(
    field: T,
    condition: WhereFilterComparisonOp,
    value: RootModel[K][T] | Observable<RootModel[K][T]>,
  ): Query<K>;
  where<T extends keyof RootModel[K]>(
    field: T,
    condition: WhereFilterListOp,
    values: RootModel[K][T][] | Observable<RootModel[K][T][]>,
  ): Query<K>;
  where<T extends keyof RootModel[K]>(
    field: T,
    condition: WhereFilterOp,
    value: any,
  ): Query<K> {
    const newParams: Observable<QueryParams<RootModel[K]>> = toObservable(
      value,
    ).pipe(
      distinctUntilChanged(),
      map(v => {
        const param: QueryParams<RootModel[K]>[T] =
          condition === 'in'
            ? { name: field, condition, values: v }
            : { name: field, condition, value: v };

        return { [field]: param } as any;
      }),
    );

    if (this.params === undefined) {
      this.params = newParams;
    } else {
      this.params = this.params.pipe(
        combineLatest(newParams, (params, p) => Object.assign({}, params, p)),
        distinctUntilChanged(),
      );
    }

    return this;
  }

  // orderBy(field: string, direction: OrderByDirection = 'asc'): Query<K> {
  //   this.explicitOrderBy = { field, direction };
  //   return this;
  // }

  limit(n: number | Observable<number>): Query<K> {
    if (this.limitValue) {
      throw new Error("You can't call `limit` twice.");
    }
    this.limitValue = toObservable(n).pipe(distinctUntilChanged());

    return this;
  }

  get query(): {
    limit?: Observable<number | undefined>;
    offset?: Observable<number | undefined>;
    params?: Observable<QueryParams<RootModel[K]> | undefined>;
  } {
    return {
      ...this.limitValue ? { limit: this.limitValue } : {},
      ...this.offsetValue ? { offset: this.offsetValue } : {},
      ...this.params ? { params: this.params } : {},
    };
  }
}



// WEBPACK FOOTER //
// ./src/app/@shared/datastore/query.ts